export type MediaOrientation = "portrait" | "landscape";

export const normalizeMediaOrientation = (value: unknown): MediaOrientation =>
  value === "portrait" ? "portrait" : "landscape";

export const mediaOrientationFromDimensions = (
  width: number,
  height: number,
): MediaOrientation => (height > width ? "portrait" : "landscape");

export const mediaOrientationAspectRatio = (
  orientation: MediaOrientation,
): string => (orientation === "portrait" ? "9 / 16" : "16 / 9");

export const shouldApplyAutoMediaOrientation = (
  manuallySelected: boolean,
): boolean => !manuallySelected;

const objectUrlFor = (file: File): string | null => {
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
};

const revokeObjectUrl = (url: string | null): void => {
  if (url == null) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    // A failed cleanup must not block the media upload flow.
  }
};

export const detectImageMediaOrientation = async (
  file: File,
): Promise<MediaOrientation> => {
  const url = objectUrlFor(file);
  if (url == null) return "landscape";
  return await new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      revokeObjectUrl(url);
      resolve(mediaOrientationFromDimensions(image.naturalWidth, image.naturalHeight));
    };
    image.onerror = () => {
      revokeObjectUrl(url);
      resolve("landscape");
    };
    image.src = url;
  });
};

export const detectVideoMediaOrientation = async (
  file: File,
): Promise<MediaOrientation> => {
  const url = objectUrlFor(file);
  if (url == null) return "landscape";
  return await new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      revokeObjectUrl(url);
      resolve(mediaOrientationFromDimensions(video.videoWidth, video.videoHeight));
    };
    video.onerror = () => {
      revokeObjectUrl(url);
      resolve("landscape");
    };
    video.src = url;
  });
};
